import { type FC } from "react";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import { Box, IconButton, InputAdornment, Stack, TextField, Typography } from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import { useUser } from "@packsize/authentication";

import {
	useProductList,
	populateProductCheckValue,
} from "@/views/configuration/server-settings/modals/add-api-connection/product-list";

import { ScopeCheckboxList } from "../../components/scope-checkbox-list";

interface OAuthInfoViewProps {
	readonly name: string;
	readonly clientId: string;
	readonly scope: string[];
	readonly onCopyToClipboard: (value: string) => void;
}

export const OAuthInfoView: FC<OAuthInfoViewProps> = ({ name, clientId, scope, onCopyToClipboard }) => {
	const intl = useIntl();
	const { hasLicense } = useUser();
	const productList = useProductList(hasLicense);
	const filteredProductsList = productList.filter((product) => product.hasLicense());

	return (
		<Stack spacing={3}>
			<Typography sx={{ pb: 1 }}>
				<FormattedMessage
					id="Use the following information to connect to the API."
					defaultMessage="Use the following information to connect to the API."
				/>
			</Typography>

			<TextField
				label={intl.formatMessage({ id: "Name", defaultMessage: "Name" })}
				value={name}
				slotProps={{
					input: {
						readOnly: true,
					},
				}}
				fullWidth
			/>

			<TextField
				label={intl.formatMessage({ id: "Client ID", defaultMessage: "Client ID" })}
				value={clientId}
				slotProps={{
					input: {
						readOnly: true,
						endAdornment: (
							<InputAdornment position="end">
								<IconButton
									size="small"
									onClick={() => onCopyToClipboard(clientId)}
									data-pendoid="connection-info__button__copy-client-id"
								>
									<ContentCopyOutlinedIcon sx={{ fontSize: 20 }} />
								</IconButton>
							</InputAdornment>
						),
					},
				}}
				fullWidth
			/>

			<Box>
				<Typography sx={{ mb: 1, fontWeight: 500 }}>
					<FormattedMessage id="Services" defaultMessage="Services" />
				</Typography>
				<ScopeCheckboxList scopes={populateProductCheckValue(filteredProductsList, scope)} readonly />
			</Box>
		</Stack>
	);
};
